/**
 * TokenPicker — Universal Solana token selector
 *
 * Supports:
 * - Popular token quick-select grid
 * - Jupiter token list search (debounced)
 * - Raw mint address paste with validation
 * - Price impact warning (yellow >2%, red >5%, blocked >10%)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  USDC_MINT,
  buildCuratedTokenList,
  labelTokenTier,
  type TokenInfo,
  type TokenTier,
} from '../constants/tokens';
import { getJupiterQuote } from '../services/api';

interface TokenPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (token: TokenInfo) => void;
  /** Currently selected token (highlighted) */
  currentMint?: string;
  /** Project default token (shown first in popular) */
  projectToken?: TokenInfo;
}

interface JupiterToken {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoURI?: string;
}

type TokenFilter = 'all' | TokenTier;

// Jupiter verified token list cache
let cachedTokenList: JupiterToken[] = [];
let cacheTimestamp = 0;
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

async function fetchJupiterTokenList(): Promise<JupiterToken[]> {
  if (cachedTokenList.length > 0 && Date.now() - cacheTimestamp < CACHE_TTL) {
    return cachedTokenList;
  }
  try {
    const res = await fetch('https://tokens.jup.ag/tokens?tags=verified');
    if (!res.ok) return cachedTokenList;
    cachedTokenList = await res.json();
    cacheTimestamp = Date.now();
    return cachedTokenList;
  } catch {
    return cachedTokenList;
  }
}

export default function TokenPicker({ isOpen, onClose, onSelect, currentMint, projectToken }: TokenPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TokenInfo[]>([]);
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState<TokenFilter>('all');
  const [mintInput, setMintInput] = useState('');
  const [mintValidating, setMintValidating] = useState(false);
  const [mintError, setMintError] = useState('');
  const [mintWarning, setMintWarning] = useState('');
  const [totalMatches, setTotalMatches] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus search on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setFilter('all');
      setMintInput('');
      setMintError('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      setTotalMatches(0);
      return;
    }

    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const tokens = await fetchJupiterTokenList();
        const curatedTokens = buildCuratedTokenList(projectToken);
        const q = query.toLowerCase();
        const verifiedMatches: TokenInfo[] = tokens.filter(t =>
          t.symbol.toLowerCase().includes(q) ||
          t.name.toLowerCase().includes(q)
        ).map(t => ({
          mint: t.address,
          symbol: t.symbol,
          name: t.name,
          decimals: t.decimals,
          logoUri: t.logoURI || '',
          tier: 'verified',
        }));
        const curatedMatches = curatedTokens.filter(t =>
          t.symbol.toLowerCase().includes(q) ||
          t.name.toLowerCase().includes(q)
        );
        const allMatches = [...curatedMatches, ...verifiedMatches].filter((token, index, list) =>
          list.findIndex(candidate => candidate.mint === token.mint) === index
        );
        setTotalMatches(allMatches.length);
        setResults(allMatches.slice(0, 15));
      } catch {
        setResults([]);
        setTotalMatches(0);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [query]);

  // Validate pasted mint
  const validateMint = useCallback(async (mint: string) => {
    if (!mint || mint.length < 32) {
      setMintError('');
      return;
    }

    setMintValidating(true);
    setMintError('');
    setMintWarning('');
    try {
      // Check Jupiter has a route
      const quote = await getJupiterQuote({
        inputMint: USDC_MINT,
        outputMint: mint,
        amount: '1000000', // 1 USDC
        slippageBps: 100,
      });

      if (!quote) {
        setMintError('No Jupiter liquidity for this token');
        return;
      }

      // Try to find token info from Jupiter list
      const tokens = await fetchJupiterTokenList();
      const found = tokens.find(t => t.address === mint);
      const curated = buildCuratedTokenList(projectToken).find(t => t.mint === mint);

      if (!found) {
        setMintWarning('Unverified token — check liquidity before buying');
      }

      onSelect({
        mint,
        symbol: curated?.symbol || found?.symbol || mint.slice(0, 6),
        name: curated?.name || found?.name || 'Unknown Token',
        decimals: curated?.decimals || found?.decimals || 6,
        logoUri: curated?.logoUri || found?.logoURI || '',
        tier: curated?.tier || (found ? 'verified' : 'community'),
      });
      onClose();
    } catch {
      setMintError('Invalid mint or no liquidity');
    } finally {
      setMintValidating(false);
    }
  }, [onSelect, onClose]);

  if (!isOpen) return null;

  // Build popular list with project token first
  const curatedTokens = buildCuratedTokenList(projectToken);
  const filteredCuratedTokens = filter === 'all'
    ? curatedTokens
    : curatedTokens.filter(token => token.tier === filter);
  const filteredResults = filter === 'all'
    ? results
    : results.filter(token => token.tier === filter);

  const displayList = filteredResults.length > 0
    ? filteredResults
    : (query.length >= 2 ? [] : filteredCuratedTokens);

  const xpFont: React.CSSProperties = { fontFamily: 'var(--sw4p-font-body)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.55)' }} />
      <div
        className="relative w-full max-w-md max-h-[80vh] flex flex-col"
        style={{
          background: '#FAFBFC',
          border: '1px solid var(--xp-border-input)',
          borderRadius: 6,
          boxShadow: 'var(--xp-shadow-window)',
          ...xpFont,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* XP title bar */}
        <div style={{
          background: 'var(--xp-titlebar-flat)',
          color: '#fff',
          padding: '6px 12px',
          fontSize: 13,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTopLeftRadius: 5,
          borderTopRightRadius: 5,
          borderBottom: '2px solid var(--tenant-accent, var(--xp-titlebar-flat))',
          flexShrink: 0,
        }}>
          <span>Select Token</span>
          <button
            onClick={onClose}
            style={{
              background: 'var(--xp-close-red)', border: 'none', cursor: 'pointer',
              color: '#fff', width: 22, height: 22, borderRadius: 3,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1 1L9 9M9 1L1 9" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Search + filters */}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--xp-border-card)', flexShrink: 0 }}>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name or symbol..."
            className="xp-input"
            style={{ marginBottom: 8 }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {([
              { id: 'all', label: 'All' },
              { id: 'project', label: 'Project' },
              { id: 'verified', label: 'Verified' },
              { id: 'community', label: 'Community' },
            ] as const).map((option) => {
              const active = filter === option.id;
              const disabled = option.id === 'project' && !projectToken;
              return (
                <button
                  key={option.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => setFilter(option.id)}
                  style={{
                    ...xpFont,
                    fontSize: 11, padding: '2px 8px', borderRadius: 2,
                    border: active ? '1px solid var(--tenant-accent, var(--xp-titlebar-flat))' : '1px solid var(--xp-border-card)',
                    background: active ? 'color-mix(in srgb, var(--tenant-accent, #0046A4) 15%, #fff)' : '#fff',
                    color: active ? 'var(--xp-text-ink)' : 'var(--xp-text-gray)',
                    fontWeight: active ? 700 : 400,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.4 : 1,
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Token list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
          {searching && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
              <span className="xp-spinner" />
            </div>
          )}

          {!searching && query.length === 1 && (
            <p style={{ fontSize: 12, color: 'var(--xp-text-gray)', textAlign: 'center', padding: '12px 0' }}>
              Type 2+ characters to search
            </p>
          )}

          {!searching && displayList.length === 0 && query.length >= 2 && (
            <p style={{ fontSize: 12, color: 'var(--xp-text-gray)', textAlign: 'center', padding: '20px 0' }}>
              No tokens found
            </p>
          )}

          {!searching && query.length < 2 && results.length === 0 && (
            <p style={{ fontSize: 11, color: 'var(--xp-text-gray)', padding: '4px 4px 2px' }}>
              Curated Token List
            </p>
          )}

          {displayList.map(token => (
            <button
              key={token.mint}
              onClick={() => { onSelect(token); onClose(); }}
              style={{
                ...xpFont,
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 8px', marginBottom: 2, borderRadius: 2,
                background: token.mint === currentMint ? '#D3E5FA' : '#fff',
                border: token.mint === currentMint
                  ? '1px solid var(--tenant-accent, var(--xp-titlebar-flat))'
                  : '1px solid transparent',
                cursor: 'pointer', textAlign: 'left',
              }}
              onMouseEnter={e => { if (token.mint !== currentMint) e.currentTarget.style.background = '#EDF4FE'; }}
              onMouseLeave={e => { if (token.mint !== currentMint) e.currentTarget.style.background = '#fff'; }}
            >
              {token.logoUri ? (
                <img
                  src={token.logoUri}
                  alt={token.symbol}
                  style={{ width: 28, height: 28, borderRadius: '50%', background: '#e0e0e0', flexShrink: 0 }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: '#D3E5FA',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: 'var(--xp-titlebar-flat)', flexShrink: 0,
                }}>
                  {token.symbol.charAt(0)}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--xp-text-ink)' }}>{token.symbol}</span>
                  <span style={{
                    fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.06em',
                    padding: '1px 5px', borderRadius: 2, fontWeight: 700,
                    background: token.tier === 'project' ? '#D3E5FA' : token.tier === 'verified' ? '#C1F0B7' : '#FFEAB3',
                    color: token.tier === 'project' ? '#003C74' : token.tier === 'verified' ? '#1B4D04' : '#5C3A00',
                    border: `1px solid ${token.tier === 'project' ? '#94BAE7' : token.tier === 'verified' ? '#3C8D0D' : '#E5A100'}`,
                  }}>
                    {labelTokenTier(token.tier)}
                  </span>
                </div>
                <p style={{ fontSize: 11, color: 'var(--xp-text-gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {token.name}
                </p>
              </div>
              {token.mint === currentMint && (
                <span style={{ fontSize: 11, color: 'var(--tenant-accent, var(--xp-titlebar-flat))', fontWeight: 700, flexShrink: 0 }}>
                  Selected
                </span>
              )}
            </button>
          ))}

          {!searching && results.length > 0 && totalMatches > results.length && (
            <p style={{ fontSize: 11, color: 'var(--xp-text-gray)', textAlign: 'center', padding: '6px 0' }}>
              Showing {results.length} of {totalMatches} matches
            </p>
          )}
        </div>

        {/* Paste mint address */}
        <div style={{ padding: '10px 12px', borderTop: '1px solid var(--xp-border-card)', flexShrink: 0 }}>
          <p style={{ fontSize: 11, color: 'var(--xp-text-gray)', marginBottom: 6 }}>
            Or paste a Solana token mint address
          </p>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              value={mintInput}
              onChange={e => { setMintInput(e.target.value); setMintError(''); }}
              placeholder="Token mint address..."
              className="xp-input"
              style={{ flex: 1 }}
            />
            <button
              onClick={() => validateMint(mintInput)}
              disabled={mintValidating || !mintInput}
              className="btn-card btn-card--primary"
              style={{ padding: '4px 12px', minHeight: 0, fontSize: 12 }}
            >
              {mintValidating ? '...' : 'Go'}
            </button>
          </div>
          {mintError && <p style={{ fontSize: 11, color: '#b81c1c', marginTop: 4 }}>{mintError}</p>}
          {mintWarning && <p style={{ fontSize: 11, color: '#7a5800', marginTop: 4 }}>Warning: {mintWarning}</p>}
        </div>
      </div>
    </div>
  );
}
